/*
  Flowkeeper - Pomodoro timer for power users and teams
  Copyright (c) 2023 Constantine Kulak, Geoffrey Espagne

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU General Public License as published by
  the Free Software Foundation; either version 3 of the License, or
  (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU General Public License for more details.

  You should have received a copy of the GNU General Public License
  along with this program.  If not, see <https://www.gnu.org/licenses/>.
*/

import {useContext, useState} from 'react';
import {
    ActionGroup,
    Button,
    ButtonType,
    ButtonVariant,
    Form,
    FormContextProvider,
    InputGroup,
    InputGroupItem,
    Modal,  
    ModalVariant,
    Spinner,
    TextInput
} from '@patternfly/react-core';
import EyeIcon from '@patternfly/react-icons/dist/esm/icons/eye-icon';
import EyeSlashIcon from '@patternfly/react-icons/dist/esm/icons/eye-slash-icon';

import DataContext from '../context/DataContext';

import {generateCachedKey, initLocalEncryptionCache} from '../../fk/core/encryption_utils';
import {resetEventSourceData } from '../../fk/core/websocket_event_source';

const Cookies = require("js-cookie");
const URLSafeBase64 = require('urlsafe-base64');

type SettingsPageProps = {
    isOpen: boolean,
    setIsOpen: Function
}

SettingsPage.defaultProps = {
    isOpen: false,
    setIsOpen: () => {}
}

function SettingsPage(props: SettingsPageProps) {

    let key = Cookies.get('fw-ukey');

    const {setData} = useContext(DataContext);

    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [passwordHidden, setPasswordHidden] = useState(true);

    const warning_text = `WARNING: Flowkeeper.org requires end-to-end data encryption. This mechanism ensures \n
                        that nobody (including Flowkeeper developers) can have access to your online data, \n
                        which is transmitted and stored in encrypted form. To encrypt and decrypt your data \n
                        Flowkeeper uses a key, which is stored ONLY ON THIS COMPUTER. A default secure random \n
                        key is generated during Flowkeeper installation.\n\n
                        
                        If you lose this key, nobody in the world will be able to decrypt your existing data, \n
                        which will be lost forever! Although this key is stored in the settings, which should \n
                        survive Flowkeeper re-installation on this computer, we HIGHLY recommend you to store a \n
                        copy of it in a safe place, in case something happens with your disk or operating \n
                        system.\n\n
                        
                        Alternatively, you can also choose your own key and treat it like a password, just \n
                        note that you will NOT be able to restore it if you forget it.\n\n
                        
                        Finally, you will have to provide the same key in all your Flowkeeper apps, which \n
                        connect to this flowkeeper.org account.`;

    const onSave = (pwd: string) => {
        setIsButtonDisabled(!isButtonDisabled);
        generateCachedKey(pwd, (err: Error, derivedKey: Buffer) => {
            if(err) throw err;
            const key = URLSafeBase64.encode(derivedKey);
            Cookies.set('fw-ukey', pwd, {expires: 30});
            Cookies.set('fw-ucachedkey', key, {expires: 30});
            setIsButtonDisabled(false);
            initLocalEncryptionCache();
            resetEventSourceData(setData);
            props.setIsOpen(false);
        });
    }

    const onCancel = (_event: KeyboardEvent | React.MouseEvent) => {
        props.setIsOpen(!props.isOpen);
    };

    return (
        <Modal
            variant={ModalVariant.medium}
            title={"Settings"}
            isOpen={props.isOpen}
            showClose={false}
            >
            <FormContextProvider initialValues={{'key': key}}>
                {({setValue, getValue, setError}) => (
                    <Form>
                        End-to-en encryption key: 
                        <InputGroup>
                            <InputGroupItem isFill>
                                <TextInput
                                    id="key"
                                    onChange={(_event, value) => {
                                        setValue('key', value);
                                        setError('key', undefined);
                                    }}
                                    type={passwordHidden ? 'password' : 'text'}
                                    value={getValue('key')}/>
                            </InputGroupItem>
                            <InputGroupItem>
                                <Button
                                    variant="control"
                                    onClick={() => setPasswordHidden(!passwordHidden)}
                                    aria-label={passwordHidden ? 'Show password' : 'Hide password'}
                                    >
                                    {passwordHidden ? <EyeIcon /> : <EyeSlashIcon />}
                                </Button>
                            </InputGroupItem>
                        </InputGroup>
                        <ActionGroup>
                            <Button
                                type={ButtonType.submit}
                                isDisabled={isButtonDisabled}
                                onClick={(e) => {
                                    e.preventDefault();   
                                    onSave(getValue('key'));
                                }}>Save</Button>
                            <Button
                                type={ButtonType.button}
                                isDisabled={isButtonDisabled}
                                variant={ButtonVariant.link}
                                onClick={(e) => {
                                    e.preventDefault();
                                    onCancel(e);
                                }}>Cancel</Button>
                            {isButtonDisabled && <Spinner size="lg" aria-label="large-spinner" isInline/>}
                        </ActionGroup>
                    </Form>)}
            </FormContextProvider>    
        </Modal>
    );
}

export default SettingsPage;
